/**
 * Imperative stepUp coordinator. Decouples the axios interceptor
 * (which can't useDispatch) from the React-mounted StepUpDialog.
 *
 * Flow:
 *   - Interceptor catches 401 + {error: "mfa_required"} and calls
 *     stepUpManager.request(). It receives a promise.
 *   - The manager notifies subscribers (the dialog) that step-up is
 *     pending. The dialog mounts, the user verifies (or cancels).
 *   - The dialog calls stepUpManager.resolve(true | false) when done.
 *     The interceptor's promise settles with that value.
 *   - On true, the interceptor replays the original request. On false,
 *     it lets the original 401 propagate.
 *
 * Coalescing: if two requests fail concurrently, both await the same
 * single dialog session. One promise object is shared until resolve()
 * fires, then a fresh promise is created for the next request.
 */

type Resolver = (success: boolean) => void;
type Listener = (open: boolean) => void;

let pendingResolver: Resolver | null = null;
let pendingPromise: Promise<boolean> | null = null;
const listeners = new Set<Listener>();

export const stepUpManager = {
  /**
   * Returns a promise that resolves to true (verified) or false
   * (cancelled). Multiple concurrent callers coalesce onto a single
   * dialog session — they all see the same outcome.
   */
  request(): Promise<boolean> {
    if (pendingPromise) return pendingPromise;
    pendingPromise = new Promise<boolean>((resolve) => {
      pendingResolver = resolve;
    });
    listeners.forEach((l) => l(true));
    return pendingPromise;
  },

  /** Called by the dialog on user verify (true) or cancel (false). */
  resolve(success: boolean) {
    const r = pendingResolver;
    pendingResolver = null;
    pendingPromise = null;
    listeners.forEach((l) => l(false));
    r?.(success);
  },

  /** The dialog subscribes to track open/closed state. */
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};

/**
 * Pre-flight step-up gate for sensitive client-side actions that
 * bypass our Go API (e.g. supabase.auth.updateUser for password
 * change, the delete_current_user RPC). The API interceptor handles
 * step-up automatically for Go-API routes, but Supabase-direct calls
 * need an explicit gate.
 *
 * Returns true if the caller can proceed:
 *   - User has no enrolled factor (AAL2 doesn't apply).
 *   - User is already at AAL2.
 *   - User successfully verified via the step-up dialog.
 *
 * Returns false if the user is AAL1 with a factor and cancelled the
 * step-up dialog. Caller should bail out without calling the
 * sensitive action.
 */
export const requireStepUp = async (): Promise<boolean> => {
  // Lazy import to avoid a circular dependency between
  // stepUpManager (shared/utils) and supabaseAPI (services).
  const { getCurrentAal, listMfaFactors } = await import(
    "@services/supabaseAPI"
  );
  const [{ current }, factors] = await Promise.all([
    getCurrentAal(),
    listMfaFactors(),
  ]);
  if (factors.length === 0) return true;
  if (current === "aal2") return true;
  return stepUpManager.request();
};
